import React, { useEffect, useState, useRef, useLayoutEffect, useCallback, useMemo } from "react";
import { useInView } from "react-intersection-observer";
import classNames from "classnames";
import moment from "moment";
//import mapboxgl from "mapbox-gl";
import { TimeState, VenueAvailabilityClient, TimeChunkSize, ReservationCalendarWorkspaceModel, ReservationCalendarTimeChunkStateModel, ZoneInfo } from "Services/API/API";
import { useApiClient } from "Services/API/useApiClient";
import LsLoading, { useLsLoading } from "Components/LsLoading";
import * as Icons from "Components/Icons";
import { DatePickerInput } from "Components/Inputs";
import { FavoriteWidget } from "Components/Favorite/FavoriteWidget";
import WorkspaceSelector from "Components/WorkspaceSelector/WorkspaceSelector";
import WorkspaceSelectorItem, { useDuplicatedNames } from "Components/WorkspaceSelector/WorkspaceSelectorItem";
import { Color } from "Components/Favorite/FavoriteButton";
import useMediaBreakpointDown from "Hooks/useMediaBreakpointDown";
import { toTimeZoneInfo } from "Utils/date";
import localMomentDate from "Utils/moment/localMomentDate";
//import { MarkerIcon, ControlPosition, useMapbox } from "Utils/useMapbox";
import { DefaultSelection, ReservationCalendarSelectionState } from "./ReservationCalendarSelection";

const COLUMN_COUNT = 24;
const COLLAPSED_ROWS_COUNT = 4;

interface ReservationCalendarScrollState {
   canScrollLeft: boolean,
   canScrollRight: boolean,
   offset: number
}

interface ReservationCalendarBodyProps {
   venueId: Guid,
   /** Venue local date in yyyy-MM-DD format */
   date?: string,
   timeZone?: ZoneInfo,
   fixedWidth?: number,
   cellSize?: number,
   cellPadding?: number,
   initialDisplayColumn?: number,
   className?: string,
   container?: HTMLElement  | string,
   onSelectionChanged?: (selection: ReservationCalendarSelectionState) => void
   onDateChanged?: (date: moment.Moment) => void
}

export const ReservationCalendarBody: React.FC<ReservationCalendarBodyProps> = ({ venueId, date, timeZone, className, container, onSelectionChanged, onDateChanged, fixedWidth = 285, cellSize = 64, cellPadding = 12, initialDisplayColumn = 7 }) => {
   const isMobile = useMediaBreakpointDown("sm");
   const venueAvailabilityClient = useApiClient(VenueAvailabilityClient);
   const [wrapperClass, isLoading, setIsLoading] = useLsLoading(true);
   const [selectedDate, setSelectedDate] = useState<moment.Moment>(() => {
      console.log(date);

      if (date) {
         if (LiquidSpaceConfig.DEBUG && /^\d{4}-\d{2}-\d{2}$/.test(date) !== true) {
            console.error(`Bad date format: ${date}`)
         }
         return moment(date).startOf("day");
      }
      else {
         const currentBrowserTime = new Date();
         const currentVenueTime = timeZone
            ? toTimeZoneInfo(currentBrowserTime, timeZone)
            //fallback
            : currentBrowserTime;

         return moment(currentVenueTime).startOf("day");
      }
   })
   const [workspaces, setWorkspaces] = useState<ReservationCalendarWorkspaceModel[]>([]);
   const [collapsed, setCollapsed] = useState<boolean>(true);
   const [selection, setSelection] = useState<ReservationCalendarSelectionState>(DefaultSelection);

   const scrollableRef = useRef<HTMLDivElement | null | undefined>();
   const scrollableInnerRef = useRef<HTMLDivElement>(null);
   const [scrollState, setScrollState] = useState<ReservationCalendarScrollState>({
      canScrollLeft: false,
      canScrollRight: false,
      offset: 0
   });

   const [setInViewElement, isCalendarVisible] = useInView();
   const scrollableRefSetter = useCallback((node: HTMLDivElement | null | undefined) => {
      setInViewElement(node);
      scrollableRef.current = node;
   }, []);

   const loadAvailability = async () => {
      setIsLoading(true);
      setSelection(DefaultSelection);
      try {
         setWorkspaces(await venueAvailabilityClient.hourlyAvailability(venueId,
            localMomentDate(selectedDate).toISOString(),
            TimeChunkSize.Min60));
      }
      catch {
      }
      setIsLoading(false);
   }

   const getColClassNames = (row: ReservationCalendarWorkspaceModel, col: ReservationCalendarTimeChunkStateModel, index: number) => {
      const selectionClass = getSelectionClass(row, index);
      if (selectionClass)
         return selectionClass;

      switch (col.state) {
         case TimeState.Available:
            return "rc-col-available";
         case TimeState.Booked:
            return "rc-col-booked";
         case TimeState.Own:
            return "rc-col-own";
         case TimeState.Closed:
         default:
            return "rc-col-closed";
      }
   }

   const getSelectionLeftAndRightIndex = (endColIndexOverride?: number): [number | undefined, number | undefined] => {
      const endIndex = endColIndexOverride || selection.endColIndex;
      if (selection.startColIndex === undefined || endIndex === undefined)
         return [undefined, undefined];

      let leftIndex, rightIndex;
      if (selection.startColIndex > endIndex) {
         leftIndex = endIndex;
         rightIndex = selection.startColIndex;
      } else {
         leftIndex = selection.startColIndex;
         rightIndex = endIndex;
      }
      return [leftIndex, rightIndex];
   }

   const onColMouseDown = (e: React.SyntheticEvent, row: ReservationCalendarWorkspaceModel, col: ReservationCalendarTimeChunkStateModel, index: number, allowDrag = false) => {
      if (col.state != TimeState.Available)
         return;

      if (allowDrag && selection.rowId == row.workspace.id) {
         e.stopPropagation();

         if (selection.startColIndex == index) {
            setSelection((currentSelection) => {
               return { ...currentSelection, startColIndex: currentSelection.endColIndex, endColIndex: currentSelection.startColIndex, mouseDown: true }
            });
            window.document.addEventListener("mouseup", onColMouseUp);
            return;
         }
         if (selection.endColIndex == index) {
            setSelection((currentSelection) => {
               return { ...currentSelection, mouseDown: true }
            });
            window.document.addEventListener("mouseup", onColMouseUp);
            return;
         }
      }

      setSelection({
         mouseDown: true,
         rowId: row.workspace.id,
         startColIndex: index,
         endColIndex: index,
      })

      /*window.document.addEventListener('mousemove', onColMouseMove);*/
      window.document.addEventListener("mouseup", onColMouseUp);
   }

   const onColMouseUp = (e) => {
      setSelection((currentSelection) => {
         return { ...currentSelection, mouseDown: false };
      })
      /*window.document.removeEventListener('mousemove', onColMouseMove);*/
      window.document.removeEventListener("mouseup", onColMouseUp);
   }

   const onColMouseMove = (e, row: ReservationCalendarWorkspaceModel, col: ReservationCalendarTimeChunkStateModel, index: number) => {
      e.preventDefault();
      if (selection.mouseDown && selection.rowId == row.workspace.id) {
         if (col.state != TimeState.Available)
            return;

         if (selection.startColIndex === undefined)
            return;

         let finalIndex = selection.startColIndex;

         if (selection.startColIndex > index) {
            for (let i = selection.startColIndex; i >= index; i--) {
               const col = row.timeChunkStates[i];
               if (col && col.state == TimeState.Available) {
                  finalIndex = i;
               } else {
                  break;
               }
            }
         }
         else {
            for (let i = selection.startColIndex; i <= index; i++) {
               const col = row.timeChunkStates[i];
               if (col && col.state == TimeState.Available) {
                  finalIndex = i;
               } else {
                  break;
               }
            }
         }

         setSelection((currentSelection) => {
            return { ...currentSelection, endColIndex: finalIndex };
         })
      }
   }

   const onColTouchStart = (e: React.SyntheticEvent, row: ReservationCalendarWorkspaceModel, col: ReservationCalendarTimeChunkStateModel, index: number, allowDrag = false) => {
      if (col.state != TimeState.Available)
         return;

      if (allowDrag && selection.rowId == row.workspace.id) {
         e.stopPropagation();

         if (selection.startColIndex == index) {
            setSelection((currentSelection) => {
               return { ...currentSelection, startColIndex: currentSelection.endColIndex, endColIndex: currentSelection.startColIndex, mouseDown: true }
            });
            window.document.addEventListener("touchend", onColTouchEnd);
            return;
         }
         if (selection.endColIndex == index) {
            setSelection((currentSelection) => {
               return { ...currentSelection, mouseDown: true }
            });
            window.document.addEventListener("touchend", onColTouchEnd);
            return;
         }
      }

      setSelection({
         mouseDown: true,
         rowId: row.workspace.id,
         startColIndex: index,
         endColIndex: index,
      })

      window.document.addEventListener("touchend", onColTouchEnd);
   }


   const onColTouchEnd = (e) => {
      setSelection((currentSelection) => {
         return { ...currentSelection, mouseDown: false };
      })
      window.document.removeEventListener("touchend", onColTouchEnd);
   }
   const getColInfoById = (rowId: string, colIndex: number): [ReservationCalendarWorkspaceModel, ReservationCalendarTimeChunkStateModel] | [undefined, undefined] => {
      if (workspaces && workspaces) {
         const row = workspaces.find(r => r.workspace.id == rowId);
         if (row && row.timeChunkStates) {
            const col = row.timeChunkStates[colIndex];
            if (col) {
               return [row, col];
            }
         }
      }
      return [undefined, undefined];

   }

   const onColTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {

      console.log("onColTouchMove", e, e.touches);
      if (e.touches.length > 0) {
         const touches = e.touches[0];
         const element = document.elementFromPoint(touches.clientX, touches.clientY) as HTMLElement;
         if (element) {
            console.log("element", element);
            const rowId = element.dataset["rowid"];
            const colIndexString = element.dataset["colindex"];
            console.log("rowId && colIndexString", rowId, colIndexString);
            if (rowId && colIndexString) {
               const index = Number.parseInt(colIndexString);
               const [row, col] = getColInfoById(rowId, index);
               console.log("row && col", row, col);
               if (row && col) {
                  if (selection.mouseDown && selection.rowId == row.workspace.id) {
                     if (col.state != TimeState.Available)
                        return;

                     if (selection.startColIndex === undefined)
                        return;

                     let finalIndex = selection.startColIndex;

                     if (selection.startColIndex > index) {
                        for (let i = selection.startColIndex; i >= index; i--) {
                           const col = row.timeChunkStates[i];
                           if (col && col.state == TimeState.Available) {
                              finalIndex = i;
                           } else {
                              break;
                           }
                        }
                     }
                     else {
                        for (let i = selection.startColIndex; i <= index; i++) {
                           const col = row.timeChunkStates[i];
                           if (col && col.state == TimeState.Available) {
                              finalIndex = i;
                           } else {
                              break;
                           }
                        }
                     }

                     setSelection((currentSelection) => {
                        return { ...currentSelection, endColIndex: finalIndex };
                     })
                  }
               }
            }
         }
      }
   }

   const getSelectionClass = (row: ReservationCalendarWorkspaceModel, index: number): string | undefined => {
      if (row.workspace.id == selection.rowId) {
         const [leftIndex, rightIndex] = getSelectionLeftAndRightIndex();
         if (leftIndex !== undefined && rightIndex !== undefined) {
            if (index >= leftIndex && index <= rightIndex) {
               let result = "rc-col-selected";

               if (index == leftIndex)
                  result += " rc-col-selected-left";
               if (index == rightIndex)
                  result += " rc-col-selected-right";

               return result;
            }
         }
      }
      return undefined;
   }

   const shouldRenderSelectionDrag = (row: ReservationCalendarWorkspaceModel, index: number, dragPosition: "left" | "right"): boolean => {
      if (row.workspace.id == selection.rowId) {
         const [leftIndex, rightIndex] = getSelectionLeftAndRightIndex();
         if (leftIndex !== undefined && rightIndex !== undefined) {
            if (dragPosition == "left" && index == leftIndex)
               return true;
            if (dragPosition == "right" && index == rightIndex)
               return true;
         }
      }
      return false;
   }

   const scrollCalendar = (marginIncrement: number) => {
      if (scrollableRef.current && scrollableInnerRef.current) {
         const newCalendarScroll = scrollState.offset + marginIncrement;
         const scrollerWrapperWidth = scrollableRef.current.offsetWidth;
         const scrollerInnerWidth = scrollableInnerRef.current.offsetWidth;
         if (scrollerInnerWidth > scrollerWrapperWidth) {
            const minMargin = scrollerWrapperWidth - scrollerInnerWidth;
            if (newCalendarScroll >= 0) {
               setScrollState({
                  canScrollLeft: false,
                  canScrollRight: true,
                  offset: 0,
               });
            }
            else if (newCalendarScroll <= minMargin) {
               setScrollState({
                  canScrollLeft: true,
                  canScrollRight: false,
                  offset: minMargin
               });
            }
            else {
               setScrollState({
                  canScrollLeft: true,
                  canScrollRight: true,
                  offset: newCalendarScroll
               });
            }
         } else {
            setScrollState({
               canScrollLeft: false,
               canScrollRight: false,
               offset: 0
            });
         }
      }
   }

   const displayRows = useMemo(() => {
      const displayRows = collapsed
         ? workspaces.slice(0, COLLAPSED_ROWS_COUNT)
         : workspaces;

      return displayRows;
   }, [workspaces, collapsed]);

   const duplicatedNames = useDuplicatedNames(displayRows);

   useLayoutEffect(() => {
      if (isCalendarVisible) {
         scrollCalendar(-1 * initialDisplayColumn * cellSize - scrollState.offset);
      }
   }, [isCalendarVisible])


   useEffect(() => {
      loadAvailability();
      if (onDateChanged)
         onDateChanged(selectedDate);
   }, [venueId, selectedDate])

   useEffect(() => {
      if (onSelectionChanged && !selection.mouseDown) {
         onSelectionChanged(selection);
      }
   }, [selection]);

   const renderDatePicker = () => <DatePickerInput
      name="rcm-datepicker"
      selected={selectedDate.toDate()}
      onChange={(value) => setSelectedDate(moment(value ? Array.isArray(value) ? value[0] : value : new Date()))}
      minDate={new Date()}
      dateFormat="E, MMMM do"
   />

   const onWorkspaceSelected = (workspaceRow: ReservationCalendarWorkspaceModel) => {
      const firstOpenSlotIndex = workspaceRow.timeChunkStates.findIndex(c => c.state == TimeState.Available);
      if (firstOpenSlotIndex > -1) {
         setSelection({
            mouseDown: false,
            rowId: workspaceRow.workspace.id,
            startColIndex: firstOpenSlotIndex,
            endColIndex: firstOpenSlotIndex,
         });
      }
      else {
         setSelection(DefaultSelection);
      }
   }

   return (<div className={classNames("reservation-calendar", { "reservation-calendar-mobile": isMobile }, className, wrapperClass)}>
      {isLoading || !workspaces
         ? <LsLoading isLoading={isLoading} />
         : isMobile
            ? <div className="rcm-inner">
               <div className="rcm-datepicker">
                  {renderDatePicker()}
               </div>
               <div className="rcm-workspaces">
                  <WorkspaceSelector workspaces={workspaces.filter(r => r.timeChunkStates.find(c => c.state == TimeState.Available) != undefined) || []} onChange={onWorkspaceSelected} value={selection.rowId} />
               </div>
            </div>
            : <div className="rc-inner">
               <div className="rc-fixed" style={{ width: `${fixedWidth}px` }}>
                  <div className="rc-row rc-header">
                     <div className="rc-col-fixed rc-col-datepicker" style={{ height: `${cellSize}px` }}>
                        {renderDatePicker()}
                     </div>
                  </div>
                  {displayRows.map(row => (<div key={"fixed" + row.workspace.id} className="rc-row">
                     <div className="rc-col-fixed rc-col-workspace" style={{ height: `${cellSize}px` }}>
                        <WorkspaceSelectorItem workspace={row.workspace} duplicatedNames={duplicatedNames} />
                        <div className="rc-workspace-favorite">
                           <FavoriteWidget color={Color.gray} venueId={venueId} workspaceId={row.workspace.id} container={container} />
                        </div>
                     </div>
                  </div>))}
               </div>
               <div className="rc-dynamic" style={{ width: `calc(100% - ${fixedWidth}px)` }}>
                  <div className={classNames("rc-scroll-button", "rc-scroll-button-left", { "rc-scroll-button--hidden": !scrollState.canScrollLeft })} onClick={() => scrollCalendar(2 * cellSize)}>
                     <Icons.ArrowLeft />
                  </div>
                  <div className={classNames("rc-scroll-button", "rc-scroll-button-right", { "rc-scroll-button--hidden": !scrollState.canScrollRight })} style={{ opacity: scrollState.canScrollRight ? 1 : 0 }} onClick={() => scrollCalendar(-2 * cellSize)}>
                     <Icons.ArrowRight />
                  </div>
                  <div className="rc-scrollable" ref={scrollableRefSetter}>
                     <div className="rc-scrollable-inner" style={{ transform: `translateX(${scrollState.offset}px)` }} ref={scrollableInnerRef}>
                        <div className="rc-row rc-header">
                           {Array(COLUMN_COUNT).fill(0).map((_, i) => {
                              const hour = moment({ hour: i });
                              return (<div className="rc-col-header" key={i} style={{ width: `${cellSize}px`, height: `${cellSize}px` }}>
                                 <div className="rc-hour">{hour.format("h")}</div>
                                 <div className="rc-ampm">{hour.format("a")}</div>
                              </div>)
                           })}
                        </div>
                        {displayRows.map(row => (<div key={"dynamic" + row.workspace.id} className="rc-row">
                           {row.timeChunkStates.map((col, i) => (<div
                              className={classNames("rc-col", getColClassNames(row, col, i))}
                              onMouseDown={(e) => onColMouseDown(e, row, col, i)}
                              onMouseMove={(e) => onColMouseMove(e, row, col, i)}
                              onTouchStart={(e) => onColTouchStart(e, row, col, i)}
                              onTouchMove={(e) => onColTouchMove(e)}
                              data-rowid={row.workspace.id}
                              data-colindex={i}
                              key={i}
                              style={{ width: `${cellSize}px`, height: `${cellSize}px`, padding: `${cellPadding}px` }}>
                              <div className="rc-col-inner" data-rowid={row.workspace.id} data-colindex={i}></div>
                              {(["left", "right"] as const).map(position => shouldRenderSelectionDrag(row, i, position)
                                 ? <div
                                    className={classNames("rc-col-selected-drag", `rc-col-selected-drag-${position}`)}
                                    onMouseDown={(e) => onColMouseDown(e, row, col, i, true)}
                                    onTouchStart={(e) => onColTouchStart(e, row, col, i, true)}
                                    key={position}><Icons.Drag /></div>
                                 : null)}
                           </div>))}
                        </div>))}
                     </div>
                  </div>
               </div>
            </div>
      }
      {!isMobile && !isLoading && workspaces.length > COLLAPSED_ROWS_COUNT && <div className="rc-collapse-toggle">
         <a href="#" onClick={(e) => { e.preventDefault(); setCollapsed((collapsed) => !collapsed); }}>
            {collapsed ? `Show ${workspaces.length - COLLAPSED_ROWS_COUNT} more` : "Close"}
            <Icons.Dropdown className={classNames("rc-collapse-toggle-icon", { "rc-collapse-toggle-icon--expanded": !collapsed })} />
         </a>
      </div>}
   </div>)
}